/**
 * Per-workshop power-user content overrides — the data model + the read-path
 * resolvers that merge an override over the static authored defaults.
 *
 * Every field is optional. A present field replaces the static default for
 * that workshop; an absent/`undefined` field falls through to the authored
 * content (the WS4 canon / placeholder factories). An all-empty record is
 * therefore a no-op — the load-bearing invariant this shares with
 * `useProgress.resolveStatus` (`src/stores/useProgress.ts`): with no
 * overrides set, every render site reads exactly what it read before.
 *
 * The record is stored on the BFF `workshops` tier (shared across devices,
 * readable by every role, writable by Facilitator/Director) via
 * `createWorkshopRecordStore` — see `src/stores/useWorkshopOverrides.ts`.
 * It reuses the existing content TYPES verbatim so the editor and the
 * renderer can't drift to two shapes.
 */

import {
  crewFromRoster,
  type CrewAccentColor,
  type CrewAgent,
  type CrewMember,
} from "@/features/workshop/crew-content";
import type { UserProfile } from "@/content/users";
import type {
  AgendaContent,
  AgendaItem,
  BringCategory,
  BringContent,
  MapContent,
  QuestionnaireContent,
  QuestionnaireQuestion,
} from "@/features/workshop/modals/modal-content";
import type { DocContent, DocSection } from "@/features/workshop/modals/doc-content";
import type { BriefItem, BriefItemStatus } from "@/features/workshop/content";
import type { OpenModal } from "@/features/workshop/modals/useWorkshopModals";
import type { PhaseObjective } from "@/features/run-sequence/run-sequence-content";
import type { WorkshopRecord } from "@/lib/create-workshop-record-store";
import type {
  BubbleCount,
  DiagramBubbleContent,
  DiagramOverride,
} from "@/features/workshop/diagram-presets";
import { BUBBLE_COUNTS } from "@/features/workshop/diagram-presets";
import {
  BookOpen,
  Calendar,
  CheckSquare,
  ClipboardCheck,
  ExternalLink,
  FileText,
  Info,
  Link as LinkIcon,
  MapPin,
  PlayCircle,
  Scale,
  Star,
  Users,
  Video,
  type LucideIcon,
} from "lucide-react";

/** Per-client choice: shared program defaults vs this client's own copy. */
export type ContentMode = "default" | "custom";

/** Fixed record id — exactly one override record per workshop bucket. Lives
 *  here (not in the store) so the sanitiser can reference it without a
 *  store↔content import cycle. */
export const OVERRIDE_ID = "overrides";

/**
 * Icons a power user can pick for an authored (brand-new) brief row. Keyed by
 * a stable serialisable name — the override stores the NAME (a `LucideIcon`
 * component can't round-trip through JSON); the renderer maps it back here.
 */
export const BRIEF_ICONS: Record<string, LucideIcon> = {
  play: PlayCircle,
  file: FileText,
  "clipboard-check": ClipboardCheck,
  calendar: Calendar,
  "map-pin": MapPin,
  "check-square": CheckSquare,
  users: Users,
  scale: Scale,
  book: BookOpen,
  link: LinkIcon,
  video: Video,
  info: Info,
  "external-link": ExternalLink,
  star: Star,
};
export const BRIEF_ICON_NAMES: string[] = Object.keys(BRIEF_ICONS);
const DEFAULT_BRIEF_ICON: LucideIcon = LinkIcon;

/**
 * Modals an authored brief row may open — a curated subset of `OpenModal`
 * (the `satisfies` proves every entry is a real, mountable modal, so a typo
 * fails the build rather than producing a dead row). A new row either opens
 * one of these OR an external URL.
 */
export const BRIEF_ACTION_MODALS = [
  "doc",
  "questionnaire",
  "agenda",
  "map",
  "bring",
  "meet-crew",
  "decision-log",
  "data-tables",
] as const satisfies readonly Exclude<OpenModal, null>[];
export type BriefActionModal = (typeof BRIEF_ACTION_MODALS)[number];

/**
 * Per-row brief override. `icon` and `modal` are intentionally NOT editable:
 * `icon` is a non-serialisable `LucideIcon` component and `modal` is the
 * structural discriminator that drives which dialog the row opens — both are
 * inherited from the matching default row (by `id`). The power user can
 * relabel, change the meta/status, hide, or reorder rows.
 */
export type BriefRowOverride = {
  id: string;
  label?: string;
  meta?: string;
  status?: BriefItemStatus;
  hidden?: boolean;
  /**
   * Authored-row fields — only meaningful when `id` is NOT one of the static
   * default rows (i.e. a brand-new row the power user added). `icon` is a
   * {@link BRIEF_ICONS} name; the row opens `modal` OR the external `url`.
   * For default rows these stay undefined (icon + action are inherited).
   */
  icon?: string;
  modal?: BriefActionModal;
  url?: string;
};

/**
 * Per-workshop crew override. "Both" model:
 *   - `rosterIds` — allow-list of roster usernames to show (the `id` field
 *     `crewFromRoster` mints). `undefined` means "show the whole roster".
 *   - `custom` — bespoke authored cards appended after the roster picks
 *     (used to carry forward WS4's hand-authored crew, or to add new cards).
 */
export type CrewOverride = {
  rosterIds?: string[];
  custom?: CrewMember[];
};

/**
 * An uploaded pre-reading document, stored as a blob on the BFF `workshops`
 * tier. When present it takes precedence over the authored `doc` sections —
 * `DocModal` renders/links the file instead. Only the reference rides in the
 * (JSON) override record; the bytes live in blob storage.
 */
export type DocUpload = {
  /** Tier-relative blob path (`<wsId>/prereading/<filename>`). */
  path: string;
  /** Original filename, for display + download. */
  filename: string;
  contentType?: string;
  size?: number;
  uploadedAt?: number;
};

/** True when an uploaded doc is a PDF (embeddable inline). */
export function isPdfUpload(upload: DocUpload): boolean {
  return (
    upload.contentType === "application/pdf" ||
    /\.pdf$/i.test(upload.filename)
  );
}

/* ── Run-sequence objective links ─────────────────────────────────────────
 * A run-sequence objective can be plain text or a clickable that links to:
 * an in-app surface (`modal`), another page on the site (`route`), an
 * external website (`url`), or an uploaded file (`file`). The default
 * launchable objectives (`{ text, launch }`) map to the `modal` kind. */
export type ObjectiveLink =
  | { kind: "modal"; modal: Exclude<OpenModal, null> }
  | { kind: "route"; route: string }
  | { kind: "url"; url: string }
  | { kind: "file"; upload: DocUpload };

export type RunSeqObjective = { text: string; link?: ObjectiveLink };

/** A pickable "page on the site" — resolves to either an in-app modal
 *  surface or an internal route. Drives the editor dropdown + the renderer. */
export type SiteDestination = {
  key: string;
  label: string;
  route?: string;
  modal?: Exclude<OpenModal, null>;
};

const WORKSHOP_ROUTE_DESTS: SiteDestination[] = (
  ["WS1", "WS2", "WS3", "WS4", "WS5", "WS6", "WS7", "WS8"] as const
).map((id, i) => ({
  key: `ws-${id}`,
  label: `Workshop ${i + 1} dashboard`,
  route: `/workshop/${id}`,
}));

export const SITE_DESTINATIONS: SiteDestination[] = [
  { key: "mc", label: "Mission Control", route: "/" },
  ...WORKSHOP_ROUTE_DESTS,
  { key: "m-doc", label: "Pre-reading (this workshop)", modal: "doc" },
  { key: "m-agenda", label: "Agenda (this workshop)", modal: "agenda" },
  { key: "m-questionnaire", label: "Questionnaire (this workshop)", modal: "questionnaire" },
  { key: "m-map", label: "Logistics (this workshop)", modal: "map" },
  { key: "m-bring", label: "What to Bring (this workshop)", modal: "bring" },
  { key: "m-meet-crew", label: "Meet the Crew (this workshop)", modal: "meet-crew" },
  { key: "m-decision-log", label: "Decisions (this workshop)", modal: "decision-log" },
];

/** Modals an objective link may target — validated by the sanitiser. Covers
 *  the site destinations above plus the default WS4 activity boards. */
const KNOWN_OBJECTIVE_MODALS = [
  "doc",
  "agenda",
  "questionnaire",
  "map",
  "bring",
  "meet-crew",
  "decision-log",
  "data-tables",
  "handshake",
  "stress-test",
] as const satisfies readonly Exclude<OpenModal, null>[];

/** Map a static default `PhaseObjective` into the editable/effective shape. */
export function defaultObjectiveToRunSeq(o: PhaseObjective): RunSeqObjective {
  return typeof o === "string"
    ? { text: o }
    : { text: o.text, link: { kind: "modal", modal: o.launch } };
}

/** Effective objectives for a phase: the power-user override list if set,
 *  else the static defaults mapped to the effective shape. */
export function resolvePhaseObjectives(
  override: WorkshopOverride | undefined,
  phaseId: string,
  defaults: PhaseObjective[],
): RunSeqObjective[] {
  const ov = override?.runSequence?.[phaseId];
  return ov ?? defaults.map(defaultObjectiveToRunSeq);
}

export type WorkshopOverride = WorkshopRecord & {
  /** Last-write timestamp (ms epoch). Drives last-write-wins when `sync()`
   *  reconciles a local edit against the BFF snapshot, so a just-saved edit
   *  isn't clobbered by a poll landing before the write propagates. */
  updatedAt?: number;
  /**
   * Whether this client uses the shared program defaults (`default`) or its
   * own editable copy (`custom`). Absent + legacy records with content
   * fields are treated as `custom` so existing overrides keep working.
   */
  contentMode?: ContentMode;
  /** Meet the Crew — who is shown. */
  crew?: CrewOverride;
  /** Brief panel item rows (relabel / meta / status / hide). */
  brief?: BriefRowOverride[];
  /** Explicit row order, by row id. Unknown ids ignored; omitted rows keep
   *  their original relative order, appended after the ordered ones. */
  briefOrder?: string[];
  /** Intro-video target URL. */
  introVideoUrl?: string;
  /** The pre-workshop questionnaire ("the questions"). */
  questionnaire?: QuestionnaireContent;
  /** Workshop agenda. */
  agenda?: AgendaContent;
  /** What to bring. */
  bring?: BringContent;
  /** Logistics (venue / map). */
  map?: MapContent;
  /** Pre-reading dossier (authored sections). */
  doc?: DocContent;
  /** Uploaded pre-reading document — wins over `doc` when present. */
  docUpload?: DocUpload;
  /** Run-sequence objective overrides, keyed by phase id. A present entry
   *  replaces that phase's objectives; absent phases keep their defaults. */
  runSequence?: Record<string, RunSeqObjective[]>;
  /** Bubble diagram layout + copy (presets for 2–7 bubbles). */
  diagram?: DiagramOverride;
};

/** Everything a power user can write — the record minus its identity keys. */
export type WorkshopOverridePatch = Partial<
  Omit<WorkshopOverride, "id" | "workshopId">
>;

/** Content fields that count as "has custom content" for legacy mode detect. */
const CONTENT_FIELD_KEYS = [
  "crew",
  "brief",
  "briefOrder",
  "introVideoUrl",
  "questionnaire",
  "agenda",
  "bring",
  "map",
  "doc",
  "docUpload",
  "runSequence",
  "diagram",
] as const satisfies ReadonlyArray<keyof WorkshopOverridePatch>;

/** Resolve default-vs-custom for a client's override record. */
export function resolveContentMode(
  override: WorkshopOverride | undefined,
): ContentMode {
  if (override?.contentMode === "custom" || override?.contentMode === "default") {
    return override.contentMode;
  }
  if (override && CONTENT_FIELD_KEYS.some((k) => override[k] !== undefined)) {
    return "custom";
  }
  return "default";
}

/** Effective diagram override when present. */
export function resolveDiagram(
  override: WorkshopOverride | undefined,
): DiagramOverride | undefined {
  return override?.diagram;
}

/** The plain replace-or-default fields (crew / brief / introVideoUrl have
 *  their own merge resolvers below). */
export type SimpleOverrideField =
  | "questionnaire"
  | "agenda"
  | "bring"
  | "map"
  | "doc";

/* ───────────────────────────── Resolvers ─────────────────────────────── */

/**
 * Effective content for a replace-style field: the override if present, else
 * the static default. The single chokepoint for agenda/map/bring/doc/
 * questionnaire — keep it dead simple so the fallback can never drift.
 */
export function resolveField<K extends SimpleOverrideField>(
  override: WorkshopOverride | undefined,
  field: K,
  fallback: NonNullable<WorkshopOverridePatch[K]>,
): NonNullable<WorkshopOverridePatch[K]> {
  return (
    (override?.[field] as NonNullable<WorkshopOverridePatch[K]> | undefined) ??
    fallback
  );
}

/**
 * Effective crew for the workshop. With no override, reproduces today's
 * chain exactly: the authored crew wins where present, otherwise the live
 * roster (`crewFromRoster`). With an override, the shown crew is the picked
 * roster members (filtered by `rosterIds`, or all when absent) followed by
 * the bespoke `custom` cards. An override with empty picks AND no custom
 * cards is a deliberate "show nobody" — not a fall-through to defaults.
 */
export function resolveCrew(
  override: WorkshopOverride | undefined,
  authored: CrewMember[],
  roster: UserProfile[],
): CrewMember[] {
  const ov = override?.crew;
  if (!ov) {
    return authored.length > 0 ? authored : crewFromRoster(roster);
  }
  const rosterCrew = crewFromRoster(roster);
  const picked =
    ov.rosterIds === undefined
      ? rosterCrew
      : rosterCrew.filter((m) => ov.rosterIds!.includes(m.id));
  return [...picked, ...(ov.custom ?? [])];
}

/**
 * Effective brief rows. For DEFAULT rows: merges per-row label/meta/status
 * over the static row by `id` (icon + modal stay inherited — they're
 * structural). For AUTHORED rows (an override row whose id isn't a default):
 * builds a full row from the stored label/meta/icon-name/action. Drops hidden
 * rows, then applies an explicit order if set.
 */
export function resolveBrief(
  override: WorkshopOverride | undefined,
  defaults: BriefItem[],
): BriefItem[] {
  // No row overrides and no reorder → return the defaults untouched (same
  // reference, so a render site selecting this doesn't churn).
  if (!override?.brief?.length && !override?.briefOrder?.length) return defaults;
  const rows = override?.brief ?? [];
  const ovById = new Map(rows.map((r) => [r.id, r]));
  const defIds = new Set(defaults.map((d) => d.id));

  // 1. Default rows with per-row overlays (hidden filtered out).
  let merged: BriefItem[] = defaults
    .filter((item) => !ovById.get(item.id)?.hidden)
    .map((item) => {
      const o = ovById.get(item.id);
      if (!o) return item;
      return {
        ...item,
        ...(o.label !== undefined ? { label: o.label } : {}),
        ...(o.meta !== undefined ? { meta: o.meta } : {}),
        ...(o.status !== undefined ? { status: o.status } : {}),
      };
    });

  // 2. Authored rows — override rows whose id isn't a static default.
  for (const o of rows) {
    if (defIds.has(o.id) || o.hidden) continue;
    merged.push({
      id: o.id,
      label: o.label ?? "",
      icon: BRIEF_ICONS[o.icon ?? ""] ?? DEFAULT_BRIEF_ICON,
      ...(o.meta !== undefined ? { meta: o.meta } : {}),
      ...(o.status !== undefined ? { status: o.status } : {}),
      ...(o.modal ? { modal: o.modal } : {}),
      ...(o.url ? { videoUrl: o.url } : {}),
    });
  }

  // 3. Explicit order (covers default + authored ids).
  const order = override?.briefOrder;
  if (order && order.length > 0) {
    const pos = new Map(order.map((id, i) => [id, i] as const));
    merged = [...merged].sort(
      (a, b) =>
        (pos.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (pos.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  }
  return merged;
}

/**
 * Effective intro-video URL. A blank override is treated as "use the
 * default" so a cleared field never yields a dead link; clearing the
 * override entirely is done via `clearOverrideField`.
 */
export function resolveIntroVideoUrl(
  override: WorkshopOverride | undefined,
  fallback: string,
): string {
  const url = override?.introVideoUrl?.trim();
  return url ? url : fallback;
}

/** The uploaded pre-reading document, if one is set. When present, `DocModal`
 *  shows it instead of the authored/ default sections. */
export function resolveDocUpload(
  override: WorkshopOverride | undefined,
): DocUpload | undefined {
  return override?.docUpload;
}

/* ──────────────────────────── Sanitiser ──────────────────────────────── */
/*
 * Best-effort validation of one raw persisted record (the `sanitiseItem`
 * contract of `createWorkshopRecordStore`). Same threat model as the other
 * record stores: a present field is validated to the depth the renderer
 * iterates (the load-bearing arrays/strings) so a stale or hand-corrupted
 * blob can never crash a `.map`; anything that fails validation is dropped,
 * leaving that surface on its static default. Returns the cleaned record, or
 * `null` only when the record itself is unusable.
 */

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === "string";
const isNum = (v: unknown): v is number => typeof v === "number";

const BRIEF_STATUSES: BriefItemStatus[] = ["available", "complete", "locked"];
const CREW_COLORS: CrewAccentColor[] = [
  "blue",
  "cyan",
  "green",
  "orange",
  "melon",
  "red",
];

function sanitiseColor(v: unknown): CrewAccentColor | undefined {
  return isStr(v) && (CREW_COLORS as string[]).includes(v)
    ? (v as CrewAccentColor)
    : undefined;
}

function sanitiseAgent(v: unknown): CrewAgent | undefined {
  if (!isObj(v)) return undefined;
  if (!isStr(v.name) || !isStr(v.type) || !isStr(v.description)) return undefined;
  return {
    name: v.name,
    type: v.type,
    description: v.description,
    color: sanitiseColor(v.color) ?? "blue",
  };
}

function sanitiseCrewMember(v: unknown): CrewMember | null {
  if (!isObj(v)) return null;
  if (!isStr(v.id) || !isStr(v.name) || !isStr(v.role)) return null;
  const member: CrewMember = { id: v.id, name: v.name, role: v.role };
  if (isStr(v.bio)) member.bio = v.bio;
  if (isStr(v.avatar)) member.avatar = v.avatar;
  if (isStr(v.badge)) member.badge = v.badge;
  const color = sanitiseColor(v.color);
  if (color) member.color = color;
  const agent = sanitiseAgent(v.agent);
  if (agent) member.agent = agent;
  return member;
}

function sanitiseCrew(v: unknown): CrewOverride | undefined {
  if (!isObj(v)) return undefined;
  const out: CrewOverride = {};
  if (Array.isArray(v.rosterIds)) out.rosterIds = v.rosterIds.filter(isStr);
  if (Array.isArray(v.custom)) {
    out.custom = v.custom
      .map(sanitiseCrewMember)
      .filter((m): m is CrewMember => m !== null);
  }
  return out.rosterIds !== undefined || out.custom !== undefined
    ? out
    : undefined;
}

function sanitiseBrief(v: unknown): BriefRowOverride[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: BriefRowOverride[] = [];
  for (const raw of v) {
    if (!isObj(raw) || !isStr(raw.id)) continue;
    const row: BriefRowOverride = { id: raw.id };
    if (isStr(raw.label)) row.label = raw.label;
    if (isStr(raw.meta)) row.meta = raw.meta;
    if (isStr(raw.status) && (BRIEF_STATUSES as string[]).includes(raw.status)) {
      row.status = raw.status as BriefItemStatus;
    }
    if (typeof raw.hidden === "boolean") row.hidden = raw.hidden;
    // Authored-row fields. `modal` is validated against the allow-list so a
    // typo/stale value can't produce a row that opens nothing (or worse).
    if (isStr(raw.icon)) row.icon = raw.icon;
    if (isStr(raw.modal) && (BRIEF_ACTION_MODALS as readonly string[]).includes(raw.modal)) {
      row.modal = raw.modal as BriefActionModal;
    }
    if (isStr(raw.url)) row.url = raw.url;
    out.push(row);
  }
  return out;
}

function sanitiseAgenda(v: unknown): AgendaContent | undefined {
  if (!isObj(v)) return undefined;
  if (!isStr(v.date) || !isStr(v.venue) || !isStr(v.timeRange)) return undefined;
  if (!Array.isArray(v.sessions)) return undefined;
  const sessions: AgendaItem[] = [];
  for (const s of v.sessions) {
    if (!isObj(s) || !isStr(s.time) || !isStr(s.title) || !isStr(s.detail)) continue;
    sessions.push({
      time: s.time,
      title: s.title,
      detail: s.detail,
      durationMin: isNum(s.durationMin) ? s.durationMin : 0,
      type: s.type === "break" ? "break" : "session",
    });
  }
  return { date: v.date, venue: v.venue, timeRange: v.timeRange, sessions };
}

function sanitiseMap(v: unknown): MapContent | undefined {
  if (!isObj(v)) return undefined;
  const keys: (keyof MapContent)[] = [
    "venueName",
    "address",
    "date",
    "time",
    "parking",
    "refreshments",
  ];
  if (!keys.every((k) => isStr(v[k]))) return undefined;
  return {
    venueName: v.venueName as string,
    address: v.address as string,
    date: v.date as string,
    time: v.time as string,
    parking: v.parking as string,
    refreshments: v.refreshments as string,
  };
}

function sanitiseBring(v: unknown): BringContent | undefined {
  if (!isObj(v) || !Array.isArray(v.categories)) return undefined;
  const categories: BringCategory[] = [];
  for (const c of v.categories) {
    if (!isObj(c) || !isStr(c.label) || !Array.isArray(c.items)) continue;
    const items = c.items
      .filter(isObj)
      .filter((it) => isStr(it.label) && isStr(it.description))
      .map((it) => ({ label: it.label as string, description: it.description as string }));
    categories.push({
      label: c.label,
      tag: c.tag === "OPTIONAL" ? "OPTIONAL" : "REQUIRED",
      items,
    });
  }
  return { categories, note: isStr(v.note) ? v.note : "" };
}

function sanitiseQuestionnaire(v: unknown): QuestionnaireContent | undefined {
  if (!isObj(v) || !Array.isArray(v.questions)) return undefined;
  const questions: QuestionnaireQuestion[] = [];
  for (const q of v.questions) {
    if (!isObj(q) || !isStr(q.id) || !isStr(q.label)) continue;
    const question: QuestionnaireQuestion = {
      id: q.id,
      label: q.label,
      type: q.type === "textarea" ? "textarea" : "input",
    };
    if (isStr(q.placeholder)) question.placeholder = q.placeholder;
    questions.push(question);
  }
  return { intro: isStr(v.intro) ? v.intro : "", questions };
}

function sanitiseDoc(v: unknown): DocContent | undefined {
  if (!isObj(v) || !isStr(v.title) || !Array.isArray(v.sections)) return undefined;
  const sections: DocSection[] = [];
  for (const s of v.sections) {
    if (!isObj(s) || !isStr(s.heading)) continue;
    const section: DocSection = { heading: s.heading };
    if (s.kind === "callout" || s.kind === "section") section.kind = s.kind;
    if (isStr(s.body)) section.body = s.body;
    else if (Array.isArray(s.body)) section.body = s.body.filter(isStr);
    if (isStr(s.intro)) section.intro = s.intro;
    if (Array.isArray(s.items)) section.items = s.items.filter(isStr);
    sections.push(section);
  }
  return { title: v.title, sections };
}

function sanitiseDocUpload(v: unknown): DocUpload | undefined {
  if (!isObj(v) || !isStr(v.path) || !isStr(v.filename)) return undefined;
  const out: DocUpload = { path: v.path, filename: v.filename };
  if (isStr(v.contentType)) out.contentType = v.contentType;
  if (isNum(v.size)) out.size = v.size;
  if (isNum(v.uploadedAt)) out.uploadedAt = v.uploadedAt;
  return out;
}

function sanitiseObjectiveLink(v: unknown): ObjectiveLink | undefined {
  if (!isObj(v)) return undefined;
  if (
    v.kind === "modal" &&
    isStr(v.modal) &&
    (KNOWN_OBJECTIVE_MODALS as readonly string[]).includes(v.modal)
  ) {
    return { kind: "modal", modal: v.modal as Exclude<OpenModal, null> };
  }
  if (v.kind === "route" && isStr(v.route)) return { kind: "route", route: v.route };
  if (v.kind === "url" && isStr(v.url)) return { kind: "url", url: v.url };
  if (v.kind === "file") {
    const upload = sanitiseDocUpload(v.upload);
    if (upload) return { kind: "file", upload };
  }
  return undefined;
}

function sanitiseRunSequence(
  v: unknown,
): Record<string, RunSeqObjective[]> | undefined {
  if (!isObj(v)) return undefined;
  const out: Record<string, RunSeqObjective[]> = {};
  for (const [phaseId, list] of Object.entries(v)) {
    if (!Array.isArray(list)) continue;
    const objectives: RunSeqObjective[] = [];
    for (const raw of list) {
      if (!isObj(raw) || !isStr(raw.text)) continue;
      const obj: RunSeqObjective = { text: raw.text };
      const link = sanitiseObjectiveLink(raw.link);
      if (link) obj.link = link;
      objectives.push(obj);
    }
    out[phaseId] = objectives;
  }
  return out;
}

export function sanitiseOverride(
  raw: Record<string, unknown>,
  fallbackWorkshopId: string,
): WorkshopOverride | null {
  const out: WorkshopOverride = {
    id: isStr(raw.id) ? raw.id : OVERRIDE_ID,
    workshopId: isStr(raw.workshopId) ? raw.workshopId : fallbackWorkshopId,
  };
  if (isNum(raw.updatedAt)) out.updatedAt = raw.updatedAt;
  const crew = sanitiseCrew(raw.crew);
  if (crew) out.crew = crew;
  const brief = sanitiseBrief(raw.brief);
  if (brief) out.brief = brief;
  if (Array.isArray(raw.briefOrder)) out.briefOrder = raw.briefOrder.filter(isStr);
  if (isStr(raw.introVideoUrl)) out.introVideoUrl = raw.introVideoUrl;
  const questionnaire = sanitiseQuestionnaire(raw.questionnaire);
  if (questionnaire) out.questionnaire = questionnaire;
  const agenda = sanitiseAgenda(raw.agenda);
  if (agenda) out.agenda = agenda;
  const bring = sanitiseBring(raw.bring);
  if (bring) out.bring = bring;
  const map = sanitiseMap(raw.map);
  if (map) out.map = map;
  const doc = sanitiseDoc(raw.doc);
  if (doc) out.doc = doc;
  const docUpload = sanitiseDocUpload(raw.docUpload);
  if (docUpload) out.docUpload = docUpload;
  const runSequence = sanitiseRunSequence(raw.runSequence);
  if (runSequence) out.runSequence = runSequence;
  const diagram = sanitiseDiagram(raw.diagram);
  if (diagram) out.diagram = diagram;
  if (raw.contentMode === "default" || raw.contentMode === "custom") {
    out.contentMode = raw.contentMode;
  }
  return out;
}

function sanitiseDiagramBubble(raw: unknown): DiagramBubbleContent | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const titleLines = Array.isArray(o.titleLines) ? o.titleLines.filter(isStr) : [];
  const bodyLines = Array.isArray(o.bodyLines) ? o.bodyLines.filter(isStr) : [];
  if (titleLines.length === 0 && bodyLines.length === 0 && !isStr(o.tipTitle)) {
    return null;
  }
  const bubble: DiagramBubbleContent = {
    id: isStr(o.id) ? o.id : "b",
    titleLines: titleLines.length ? titleLines : [""],
    bodyLines,
  };
  if (isStr(o.tipTitle)) bubble.tipTitle = o.tipTitle;
  if (isStr(o.tipBody)) bubble.tipBody = o.tipBody;
  if (isStr(o.subtitle)) bubble.subtitle = o.subtitle;
  if (isStr(o.badge)) bubble.badge = o.badge;
  if (
    o.lucide === "user" ||
    o.lucide === "users" ||
    o.lucide === "eye" ||
    o.lucide === "bot"
  ) {
    bubble.lucide = o.lucide;
  }
  return bubble;
}

function sanitiseDiagram(raw: unknown): DiagramOverride | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const count = o.bubbleCount;
  if (typeof count !== "number" || !BUBBLE_COUNTS.includes(count as BubbleCount)) {
    return null;
  }
  const bubbleCount = count as BubbleCount;
  const rawBubbles = Array.isArray(o.bubbles) ? o.bubbles : [];
  const bubbles = rawBubbles
    .map(sanitiseDiagramBubble)
    .filter((b): b is DiagramBubbleContent => b !== null)
    .slice(0, bubbleCount);
  while (bubbles.length < bubbleCount) {
    const i = bubbles.length;
    bubbles.push({
      id: `b${i + 1}`,
      titleLines: [`BUBBLE ${i + 1}`],
      bodyLines: [""],
    });
  }
  return { bubbleCount, bubbles };
}
